import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Form, Spinner } from 'react-bootstrap';
import { globalURLS } from '../../../Global/urls';
import {
    filterByMbbsFilter,
    MbbsFilter,
    normalizeSpecialties,
    SpecialtyLabel,
} from '../../Common/Inputs/TargetSpecialtiesSelector';
import '../../Common/Inputs/TargetSpecialtiesSelector.css';

export interface ScoreboardConfig {
    allownull: boolean;
    allow: string[];
}

interface Option {
    id: string;
    name: string;
    isNonMBBS?: boolean;
}

interface Props {
    title: string;
    note?: string;
    options?: Option[];
    loadOptionsOnExpand?: boolean;
    autoDefaultToAll?: boolean;
    value: ScoreboardConfig;
    onChange: (config: ScoreboardConfig) => void;
    disabled?: boolean;
}

const ScoreboardConfigSelector: React.FC<Props> = ({
    title,
    note,
    options: staticOptions = [],
    loadOptionsOnExpand = false,
    autoDefaultToAll = true,
    value,
    onChange,
    disabled = false,
}) => {
    const [expanded, setExpanded] = useState(false);
    const [targetAll, setTargetAll] = useState(true);
    const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
    const [searchTerm, setSearchTerm] = useState('');
    const [mbbsFilter, setMbbsFilter] = useState<MbbsFilter>('all');
    const [options, setOptions] = useState<Option[]>(staticOptions);
    const [loading, setLoading] = useState(false);
    const [fetchError, setFetchError] = useState(false);
    const [hasFetched, setHasFetched] = useState(!loadOptionsOnExpand);

    useEffect(() => {
        if (!loadOptionsOnExpand) {
            setOptions(staticOptions);
            setHasFetched(true);
        }
    }, [loadOptionsOnExpand, staticOptions]);

    useEffect(() => {
        const optionIds = options.map(option => option.id);
        const isAllSelected =
            optionIds.length > 0 &&
            optionIds.every(id => value.allow.includes(id));

        setTargetAll(isAllSelected);
        setSelectedIds(new Set(value.allow));
    }, [value, options]);

    const notifyChange = useCallback(
        (allownull: boolean, all: boolean, ids: Set<string>) => {
            const optionIds = options.map(option => option.id);
            onChange({
                allownull,
                allow: all ? optionIds : Array.from(ids),
            });
        },
        [onChange, options],
    );

    const fetchSpecialties = useCallback(async () => {
        setLoading(true);
        setFetchError(false);
        try {
            const resp = await axios.post(
                `${globalURLS.baseURL}getSpecialties`,
                {},
                globalURLS.config,
            );
            const raw =
                resp?.data?.data !== undefined
                    ? resp.data.data
                    : resp?.data;
            const specialties = normalizeSpecialties(raw).map(s => ({
                id: s.specialtyid,
                name: s.name,
                isNonMBBS: s.isNonMBBS,
            }));
            setOptions(specialties);
        } catch {
            setOptions([]);
            setFetchError(true);
        } finally {
            setLoading(false);
            setHasFetched(true);
        }
    }, []);

    useEffect(() => {
        const shouldFetch =
            loadOptionsOnExpand &&
            (expanded || value.allow.length > 0) &&
            !hasFetched &&
            !loading;
        if (shouldFetch) {
            fetchSpecialties();
        }
    }, [
        loadOptionsOnExpand,
        expanded,
        value.allow.length,
        hasFetched,
        loading,
        fetchSpecialties,
    ]);

    useEffect(() => {
        if (!autoDefaultToAll || options.length === 0 || value.allow.length > 0) {
            return;
        }
        notifyChange(value.allownull ?? true, true, new Set());
    }, [
        autoDefaultToAll,
        options,
        value.allow.length,
        value.allownull,
        notifyChange,
    ]);

    const filteredOptions = useMemo(() => {
        const q = searchTerm.trim().toLowerCase();
        const byMbbs = loadOptionsOnExpand
            ? filterByMbbsFilter(options, mbbsFilter)
            : options;
        if (!q) return byMbbs;
        return byMbbs.filter(option =>
            option.name.toLowerCase().includes(q),
        );
    }, [options, searchTerm, mbbsFilter, loadOptionsOnExpand]);

    const allFilteredSelected =
        filteredOptions.length > 0 &&
        filteredOptions.every(option => selectedIds.has(option.id));

    const handleExpand = () => {
        if (disabled) return;
        setExpanded(prev => !prev);
    };

    const handleAllowNullChange = () => {
        notifyChange(!value.allownull, targetAll, selectedIds);
    };

    const handleTargetAllChange = () => {
        const next = !targetAll;
        setTargetAll(next);
        if (next) {
            notifyChange(value.allownull, true, selectedIds);
        }
    };

    const toggleOne = (id: string) => {
        setSelectedIds(prev => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            if (!targetAll) notifyChange(value.allownull, false, next);
            return next;
        });
    };

    const toggleAllFiltered = () => {
        setSelectedIds(prev => {
            const next = new Set(prev);
            if (allFilteredSelected) {
                filteredOptions.forEach(option => next.delete(option.id));
            } else {
                filteredOptions.forEach(option => next.add(option.id));
            }
            if (!targetAll) notifyChange(value.allownull, false, next);
            return next;
        });
    };

    const itemLabel = title.toLowerCase();
    const summary = targetAll
        ? `All ${itemLabel}`
        : selectedIds.size === 0
          ? `No ${itemLabel} selected`
          : `${selectedIds.size} ${itemLabel} selected`;

    return (
        <div className="target-specialties-selector">
            <button
                type="button"
                className="target-specialties-header"
                onClick={handleExpand}
                disabled={disabled}
                aria-expanded={expanded}
            >
                <span className="target-specialties-title">{title}</span>
                <span className="target-specialties-summary text-muted">
                    {summary}
                </span>
                <span className="target-specialties-chevron">
                    {expanded ? '▾' : '▸'}
                </span>
            </button>
            {note && (
                <p className="target-specialties-note mb-0">{note}</p>
            )}

            {expanded && (
                <div className="target-specialties-body border rounded p-3 mt-3 bg-white">
                    <Form.Check
                        type="switch"
                        id={`${title}-allow-null`}
                        label="Allow Null"
                        checked={value.allownull}
                        disabled={disabled}
                        onChange={handleAllowNullChange}
                        className="mb-3"
                    />
                    <Form.Check
                        type="switch"
                        id={`${title}-all`}
                        label="All"
                        checked={targetAll}
                        disabled={disabled}
                        onChange={handleTargetAllChange}
                        className="mb-3"
                    />

                    {!targetAll && (
                        <>
                            <Form.Control
                                type="search"
                                placeholder={`Search ${itemLabel}`}
                                value={searchTerm}
                                disabled={disabled}
                                onChange={e => setSearchTerm(e.target.value)}
                                className="mb-3"
                                aria-label={`Search ${itemLabel}`}
                            />
                            {loadOptionsOnExpand && (
                                <Form.Select
                                    value={mbbsFilter}
                                    disabled={disabled}
                                    onChange={e =>
                                        setMbbsFilter(
                                            e.target.value as MbbsFilter,
                                        )
                                    }
                                    className="mb-3"
                                    aria-label="Filter by MBBS type"
                                >
                                    <option value="all">All specialties</option>
                                    <option value="mbbs">MBBS only</option>
                                    <option value="non-mbbs">
                                        Non-MBBS only
                                    </option>
                                </Form.Select>
                            )}

                            {loading ? (
                                <div className="d-flex justify-content-center py-3">
                                    <Spinner animation="border" size="sm" />
                                </div>
                            ) : fetchError ? (
                                <p className="text-danger mb-0">
                                    Failed to load {itemLabel}.
                                </p>
                            ) : (
                                <div className="target-specialties-list">
                                    {filteredOptions.length > 0 && (
                                        <Form.Check
                                            type="checkbox"
                                            id={`${title}-select-all`}
                                            label="Select all"
                                            checked={allFilteredSelected}
                                            disabled={disabled}
                                            onChange={toggleAllFiltered}
                                            className="mb-2 fw-semibold"
                                        />
                                    )}
                                    {filteredOptions.map(option => (
                                        <Form.Check
                                            key={option.id}
                                            type="checkbox"
                                            id={`${title}-${option.id}`}
                                            label={
                                                loadOptionsOnExpand ? (
                                                    <SpecialtyLabel
                                                        name={option.name}
                                                        isNonMBBS={
                                                            option.isNonMBBS
                                                        }
                                                    />
                                                ) : (
                                                    option.name
                                                )
                                            }
                                            checked={selectedIds.has(
                                                option.id,
                                            )}
                                            disabled={disabled}
                                            onChange={() =>
                                                toggleOne(option.id)
                                            }
                                            className="mb-1"
                                        />
                                    ))}
                                    {filteredOptions.length === 0 && (
                                        <p className="text-muted mb-0">
                                            No {itemLabel} found.
                                        </p>
                                    )}
                                </div>
                            )}
                        </>
                    )}
                </div>
            )}
        </div>
    );
};

export default ScoreboardConfigSelector;
